import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';

@Component({
  selector: 'app-central-pedidos-dialog',
  templateUrl: './central-pedidos-dialog.component.html',
  styleUrls: ['./central-pedidos-dialog.component.scss']
})
export class CentralPedidosDialogComponent implements OnChanges {
  @Input() aberto = false;
  @Input() termoBusca = '';
  @Input() buscaExecutada = false;
  @Input() buscando = false;
  @Input() modoTrocaCliente = false;
  @Input() erro = '';
  @Input() clientesAgrupados: any[] = [];
  @Input() clienteSelecionado: any = null;
  @Input() enderecos: any[] = [];
  @Input() enderecoSelecionado: any = null;
  @Input() preparando = false;
  @Input() criandoCliente = false;
  @Input() lojaDestino: any = null;
  @Input() roteamento: any = null;
  @Input() lojasParticipantes: any[] = [];

  @Output() fechar = new EventEmitter<void>();
  @Output() buscar = new EventEmitter<void>();
  @Output() alterarTermoBusca = new EventEmitter<string>();
  @Output() selecionarCliente = new EventEmitter<any>();
  @Output() trocarCliente = new EventEmitter<void>();
  @Output() selecionarEndereco = new EventEmitter<any>();
  @Output() selecionarLojaSemEndereco = new EventEmitter<any>();
  @Output() cadastrarEndereco = new EventEmitter<any>();
  @Output() criarCliente = new EventEmitter<any>();
  @Output() continuarRetiradaDestino = new EventEmitter<void>();

  @ViewChild('buscaCentralInput') buscaCentralInput?: ElementRef<HTMLInputElement>;
  @ViewChild('novoClienteNomeInput') novoClienteNomeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('novoClienteTelefoneInput') novoClienteTelefoneInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('clienteCard') clienteCards?: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChildren('enderecoCard') enderecoCards?: QueryList<ElementRef<HTMLButtonElement>>;

  clienteIndiceAtivo = -1;
  enderecoIndiceAtivo = -1;
  cadastroClienteAberto = false;
  trocaLojaCadastroVisivel = false;
  lojasCadastroComboItens: { id: any; nome: string }[] = [];
  comboFilterSettings = { operator: 'contains' as 'contains', caseSensitive: false };
  novoCliente: any = {
    nome: '',
    telefone: '',
    empresaOrigemId: null
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes.clientesAgrupados) {
      this.clienteIndiceAtivo = this.totalClientes ? 0 : -1;
    }

    if (changes.enderecos) {
      this.enderecoIndiceAtivo = this.enderecos?.length ? 0 : -1;
    }

    if (changes.lojasParticipantes) {
      this.recalculeLojasCadastroComboItens();
    }

    if (changes.aberto && this.aberto) {
      setTimeout(() => this.cadastroClienteAberto ? this.foqueNovoCliente() : this.foqueBusca(), 0);
    }

    if (changes.clienteSelecionado && this.clienteSelecionado) {
      this.cadastroClienteAberto = false;
    }
  }

  get etapaCadastroCliente(): boolean {
    return this.cadastroClienteAberto && (!this.clienteSelecionado || this.modoTrocaCliente);
  }

  get etapaEnderecos(): boolean {
    return !!this.clienteSelecionado && !this.modoTrocaCliente;
  }

  get podeBuscar(): boolean {
    return !this.buscando && !this.preparando && (this.termoBusca || '').trim().length >= 2;
  }

  get totalClientes(): number {
    return (this.clientesAgrupados || []).reduce((total, grupo) => total + ((grupo?.clientes || []).length), 0);
  }

  get lojasCadastroCliente(): any[] {
    return (this.lojasParticipantes || []).filter((loja: any) => this.valorBooleano(loja?.ativa));
  }

  private recalculeLojasCadastroComboItens() {
    this.lojasCadastroComboItens = this.lojasCadastroCliente.map((loja: any) => ({
      id: loja.id || loja.empresaId,
      nome: this.getLojaCadastroNome(loja)
    }));
  }

  get deveEscolherLojaCadastro(): boolean {
    return this.lojasCadastroCliente.length > 1;
  }

  get lojaCadastroAtual(): any {
    if (!this.lojasCadastroCliente.length) return null;
    const id = this.novoCliente?.empresaOrigemId;
    if (id) {
      const encontrada = this.lojasCadastroCliente.find((loja: any) => (loja.id || loja.empresaId) === id);
      if (encontrada) return encontrada;
    }
    return this.lojasCadastroCliente[0];
  }

  onAlternarTrocaLojaCadastro() {
    this.trocaLojaCadastroVisivel = !this.trocaLojaCadastroVisivel;
  }

  get podeCriarCliente(): boolean {
    return !this.preparando
      && !this.criandoCliente
      && (this.novoCliente?.nome || '').trim().length >= 2
      && this.normalizeTelefone(this.novoCliente?.telefone).length >= 8
      && (!!this.novoCliente?.empresaOrigemId || this.lojasCadastroCliente.length === 1);
  }

  onTermoBuscaChange(termo: string) {
    this.clienteIndiceAtivo = -1;
    this.alterarTermoBusca.emit(termo);
  }

  onBuscar() {
    if (!this.podeBuscar) return;
    this.buscar.emit();
  }

  onAbrirCadastroCliente() {
    if (this.preparando || this.criandoCliente) return;
    this.limpeNovoCliente();
    this.preenchaNovoClientePeloTermoBusca();
    this.cadastroClienteAberto = true;
    setTimeout(() => this.foqueCampoVazioDoNovoCliente(), 0);
  }

  onVoltarParaBusca() {
    if (this.preparando || this.criandoCliente) return;
    this.cadastroClienteAberto = false;
    setTimeout(() => this.foqueBusca(), 0);
  }

  onTelefoneNovoClienteChange(telefone: string) {
    this.novoCliente.telefone = this.formateTelefoneBrasileiro(telefone);
  }

  onCriarCliente() {
    if (!this.podeCriarCliente) return;

    const lojaUnica = this.lojasCadastroCliente.length === 1 ? this.lojasCadastroCliente[0] : null;
    this.criarCliente.emit({
      nome: (this.novoCliente.nome || '').trim(),
      telefone: this.normalizeTelefone(this.novoCliente.telefone),
      empresaOrigemId: this.novoCliente.empresaOrigemId || lojaUnica?.id || lojaUnica?.empresaId || null,
      codigoPais: '+55'
    });
  }

  onSelecionarClientePorClique(cliente: any, indice: number) {
    this.clienteIndiceAtivo = indice;
    this.selecionarCliente.emit(cliente);
  }

  onSelecionarEnderecoPorClique(endereco: any, indice: number) {
    this.enderecoIndiceAtivo = indice;
    this.selecionarEndereco.emit(endereco);
  }

  onSelecionarLojaSemEndereco(loja: any) {
    if (this.preparando) return;
    if (this.lojaRequerEndereco(loja)) {
      this.cadastrarEndereco.emit(loja);
      return;
    }
    if (!this.lojaPodeContinuarSemEndereco(loja)) return;
    this.selecionarLojaSemEndereco.emit(loja);
  }

  onTrocarCliente() {
    this.clienteIndiceAtivo = this.totalClientes ? 0 : -1;
    this.enderecoIndiceAtivo = -1;
    this.trocarCliente.emit();
    setTimeout(() => this.foqueBusca(), 0);
  }

  onFechar() {
    if (this.preparando || this.criandoCliente) return;
    this.fechar.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.aberto || this.preparando || this.criandoCliente) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.onFechar();
      return;
    }

    if (this.etapaEnderecos) {
      this.onKeyDownEnderecos(event);
      return;
    }

    if (this.etapaCadastroCliente) {
      this.onKeyDownCadastroCliente(event);
      return;
    }

    if (event.key === 'F4') {
      event.preventDefault();
      this.foqueBusca();
      return;
    }

    if (event.altKey && this.selecioneClientePorAtalho(event)) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.movaClienteAtivo(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.movaClienteAtivo(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmeClienteOuBusca();
    }
  }

  getClientInitial(cliente: any): string {
    return (cliente?.nome || '?').charAt(0).toUpperCase();
  }

  getNovoClienteInitial(): string {
    const nome = (this.novoCliente?.nome || '').trim();
    return nome ? nome.charAt(0).toUpperCase() : '+';
  }

  getResumoEtapaCliente(): string {
    if (this.clienteSelecionado) return this.clienteSelecionado.nome;
    if (this.etapaCadastroCliente) {
      const nome = (this.novoCliente?.nome || '').trim();
      return nome ? `Cadastrando ${nome}` : 'Cadastrando novo cliente';
    }
    return 'Selecione o cadastro do cliente';
  }

  getTelefone(cliente: any): string {
    return cliente?.telefone || 'Telefone não informado';
  }

  getLojaOrigem(cliente: any): string {
    return cliente?.lojaOrigem?.nome || cliente?.empresaNome || 'Loja do atendimento';
  }

  getEnderecoPrincipal(endereco: any): string {
    if (!endereco) return 'Endereço não informado';

    const rua = (endereco.logradouro || endereco.endereco || '').toString().trim();
    const numero = (endereco.numero || '').toString().trim();
    const complemento = (endereco.complemento || '').toString().trim();
    const base = [rua || 'Endereço', numero || 's/n'].filter(Boolean).join(', ');

    return complemento ? `${base} - ${complemento}` : base;
  }

  getEnderecoSecundario(endereco: any): string {
    if (!endereco) return '';

    const bairro = (endereco.bairro || '').toString().trim();
    const cidade = (endereco.cidade?.nome || endereco.cidade || '').toString().trim();
    const uf = (endereco.estado?.sigla || endereco.uf || '').toString().trim();
    const cep = (endereco.cep || '').toString().trim();
    const cidadeUf = [cidade, uf].filter(Boolean).join('/');

    return [bairro, cidadeUf, cep].filter(Boolean).join(' - ');
  }

  getEnderecoTipoLabel(endereco: any): string {
    return (endereco?.descricao || endereco?.tipo || endereco?.apelido || '').toString().trim();
  }

  getEnderecoTipoClasse(endereco: any): string {
    const tipo = this.getEnderecoTipoLabel(endereco).toLowerCase();
    if (tipo.includes('casa')) return 'is-home';
    if (tipo.includes('trabalho')) return 'is-work';
    return 'is-other';
  }

  get lojasSemEndereco(): any[] {
    return (this.lojasParticipantes || [])
      .filter((loja: any) => this.valorBooleano(loja?.ativa))
      .slice()
      .sort((a: any, b: any) => {
        const origemA = this.isLojaOrigemCliente(a) ? 0 : 1;
        const origemB = this.isLojaOrigemCliente(b) ? 0 : 1;
        if (origemA !== origemB) return origemA - origemB;

        const retiradaA = this.lojaPodeContinuarSemEndereco(a) ? 0 : 1;
        const retiradaB = this.lojaPodeContinuarSemEndereco(b) ? 0 : 1;
        if (retiradaA !== retiradaB) return retiradaA - retiradaB;
        return (a?.nome || '').localeCompare(b?.nome || '');
      });
  }

  get lojasRetirada(): any[] {
    return this.lojasSemEndereco.filter((loja: any) => this.lojaPodeContinuarSemEndereco(loja));
  }

  get lojasDeliveryRequerEndereco(): any[] {
    return this.lojasSemEndereco.filter((loja: any) => this.lojaRequerEndereco(loja));
  }

  get lojaComDelivery(): any {
    if (this.lojaPermiteDelivery(this.lojaDestino)) return this.lojaDestino;
    return (this.lojasParticipantes || []).find((loja: any) => this.lojaPermiteDelivery(loja)) || null;
  }

  get podeCriarNovoEndereco(): boolean {
    return !!this.lojaComDelivery;
  }

  get lojaDestinoPermiteRetiradaDireto(): boolean {
    return !!this.lojaDestino && this.lojaPodeContinuarSemEndereco(this.lojaDestino);
  }

  onContinuarRetiradaDestino() {
    if (this.preparando || !this.lojaDestino) return;
    this.continuarRetiradaDestino.emit();
  }

  onCadastrarNovoEndereco() {
    if (this.preparando) return;
    if (!this.podeCriarNovoEndereco) return;
    const loja = this.lojaComDelivery;
    const lojaId = loja?.id || loja?.empresaId;
    const destinoId = this.lojaDestino?.id || this.lojaDestino?.empresaId;
    const mesmaLoja = destinoId && String(destinoId) === String(lojaId);
    this.cadastrarEndereco.emit(mesmaLoja ? undefined : loja);
  }

  lojaPermiteDelivery(loja: any): boolean {
    if (!loja) return false;
    if (loja.deliveryAtivo === undefined && loja.retiradaAtiva === undefined) return true;
    return this.valorBooleano(loja.deliveryAtivo);
  }

  private nomesParaDestaquePrefixoCache: { chave: string; prefixo: string; sufixo: string } = { chave: '', prefixo: '', sufixo: '' };

  private get partesComunsDosNomes(): { prefixo: string; sufixo: string } {
    const nomes = this.lojasSemEndereco.map((loja: any) => (loja?.nome || '').toString());
    const chave = nomes.join('|');
    if (this.nomesParaDestaquePrefixoCache.chave === chave) {
      return this.nomesParaDestaquePrefixoCache;
    }

    let prefixo = '';
    let sufixo = '';
    if (nomes.length >= 2) {
      prefixo = this.calculePrefixoComum(nomes);
      sufixo = this.calculeSufixoComum(nomes, prefixo);
    }

    if (prefixo.length < 8) prefixo = '';
    if (sufixo.length < 8) sufixo = '';

    this.nomesParaDestaquePrefixoCache = { chave, prefixo, sufixo };
    return this.nomesParaDestaquePrefixoCache;
  }

  getNomePartes(loja: any): { prefixo: string; destaque: string; sufixo: string } {
    const nome = (loja?.nome || '').toString();
    const { prefixo, sufixo } = this.partesComunsDosNomes;

    if (!prefixo && !sufixo) return { prefixo: '', destaque: nome, sufixo: '' };

    let destaque = nome;
    if (prefixo && destaque.startsWith(prefixo)) destaque = destaque.slice(prefixo.length);
    if (sufixo && destaque.endsWith(sufixo)) destaque = destaque.slice(0, destaque.length - sufixo.length);

    if (!destaque.trim()) return { prefixo: '', destaque: nome, sufixo: '' };

    return { prefixo, destaque, sufixo };
  }

  private calculePrefixoComum(nomes: string[]): string {
    const primeiro = nomes[0];
    let i = 0;
    while (i < primeiro.length && nomes.every(n => n.charAt(i) === primeiro.charAt(i))) {
      i++;
    }
    return primeiro.slice(0, i);
  }

  private calculeSufixoComum(nomes: string[], prefixo: string): string {
    const primeiro = nomes[0];
    const limite = primeiro.length - prefixo.length;
    let i = 0;
    while (i < limite && nomes.every(n => n.charAt(n.length - 1 - i) === primeiro.charAt(primeiro.length - 1 - i))) {
      i++;
    }
    return primeiro.slice(primeiro.length - i);
  }

  lojaPermiteRetirada(loja: any): boolean {
    return this.valorBooleano(loja?.retiradaAtiva);
  }

  lojaPodeContinuarSemEndereco(loja: any): boolean {
    return this.lojaPermiteRetirada(loja) || !this.temConfiguracaoEntrega(loja);
  }

  lojaRequerEndereco(loja: any): boolean {
    return this.temConfiguracaoEntrega(loja) && !this.lojaPermiteRetirada(loja);
  }

  get lojaDestinoVisual(): any {
    return this.lojaDestino || this.lojasSemEndereco.find((loja: any) => {
      return this.isLojaOrigemCliente(loja) && this.lojaPodeContinuarSemEndereco(loja);
    }) || null;
  }

  getLojaSemEnderecoStatus(loja: any): string {
    if (this.lojaPodeContinuarSemEndereco(loja)) return 'Retirada disponível';
    return 'Delivery requer endereço';
  }

  getLojaSemEnderecoSubtitulo(loja: any): string {
    return this.isLojaOrigemCliente(loja) ? 'Loja do cliente' : 'Loja participante';
  }

  getLojaSemEnderecoAcao(loja: any): string {
    if (this.preparando && this.isLojaDestino(loja)) return 'Preparando...';
    if (this.isLojaDestino(loja) && this.lojaPodeContinuarSemEndereco(loja)) return 'Continuar';
    if (this.lojaPodeContinuarSemEndereco(loja)) return 'Selecionar para retirada';
    return 'Cadastrar endereço';
  }

  isLojaDestino(loja: any): boolean {
    const lojaId = loja?.id || loja?.empresaId;
    const destinoId = this.lojaDestino?.id || this.lojaDestino?.empresaId;
    if (destinoId) return String(lojaId || '') === String(destinoId);
    return this.isLojaOrigemCliente(loja);
  }

  isLojaSemEnderecoSelecionada(loja: any): boolean {
    return this.isLojaDestino(loja) && this.lojaPodeContinuarSemEndereco(loja);
  }

  getClienteIndiceGlobal(grupoIndex: number, clienteIndex: number): number {
    let indice = clienteIndex;

    for (let i = 0; i < grupoIndex; i++) {
      indice += (this.clientesAgrupados?.[i]?.clientes || []).length;
    }

    return indice;
  }

  getClienteAtalho(indice: number): string {
    return indice >= 0 && indice < 9 ? String(indice + 1) : '';
  }

  isClienteAtivo(indice: number): boolean {
    return this.clienteIndiceAtivo === indice;
  }

  getEnderecoAtalho(indice: number): string {
    return indice >= 0 && indice < 9 ? String(indice + 1) : '';
  }

  isEnderecoAtivo(indice: number): boolean {
    return this.enderecoIndiceAtivo === indice;
  }

  trackByGrupo(index: number, grupo: any): any {
    return grupo?.loja?.id || grupo?.loja?.nome || index;
  }

  trackByCliente(index: number, cliente: any): any {
    return cliente?.contatoId || cliente?.id || `${cliente?.empresaId || 'loja'}-${index}`;
  }

  trackByEndereco(index: number, endereco: any): any {
    return endereco?.enderecoId || endereco?.id || index;
  }

  trackByLoja(index: number, loja: any): any {
    return loja?.id || loja?.empresaId || index;
  }

  getLojaCadastroNome(loja: any): string {
    return loja?.nome || 'Loja';
  }

  private valorBooleano(valor: any): boolean {
    return valor === true || valor === 1 || valor === '1';
  }

  private normalizeTelefone(telefone: any): string {
    return telefone ? String(telefone).replace(/\D/g, '') : '';
  }

  private formateTelefoneBrasileiro(telefone: any): string {
    const digitos = this.normalizeTelefone(telefone).slice(0, 11);

    if (digitos.length <= 2) return digitos;
    if (digitos.length <= 6) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
    if (digitos.length <= 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  }

  private temConfiguracaoEntrega(loja: any): boolean {
    return loja?.retiradaAtiva !== undefined || loja?.deliveryAtivo !== undefined;
  }

  isLojaOrigemCliente(loja: any): boolean {
    const lojaId = loja?.id || loja?.empresaId;
    const lojaOrigemId = this.clienteSelecionado?.lojaOrigem?.id || this.clienteSelecionado?.empresaId;
    return String(lojaId || '') === String(lojaOrigemId || '');
  }

  private confirmeClienteOuBusca() {
    const cliente = this.obtenhaClientePorIndice(this.clienteIndiceAtivo);

    if (cliente) {
      this.selecionarCliente.emit(cliente);
      return;
    }

    this.onBuscar();
  }

  private onKeyDownEnderecos(event: KeyboardEvent) {
    if (event.key === 'F4') {
      event.preventDefault();
      this.onTrocarCliente();
      return;
    }

    if (event.altKey && this.selecioneEnderecoPorAtalho(event)) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.movaEnderecoAtivo(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.movaEnderecoAtivo(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmeEndereco();
    }
  }

  private onKeyDownCadastroCliente(event: KeyboardEvent) {
    if (event.key === 'F4') {
      event.preventDefault();
      this.onVoltarParaBusca();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.onCriarCliente();
    }
  }

  private confirmeEndereco() {
    const endereco = this.obtenhaEnderecoPorIndice(this.enderecoIndiceAtivo);
    if (!endereco) return;

    this.selecionarEndereco.emit(endereco);
  }

  private selecioneClientePorAtalho(event: KeyboardEvent): boolean {
    const indice = Number(event.key) - 1;
    if (!Number.isInteger(indice) || indice < 0 || indice > 8) return false;

    const cliente = this.obtenhaClientePorIndice(indice);
    if (!cliente) return false;

    event.preventDefault();
    this.clienteIndiceAtivo = indice;
    this.selecionarCliente.emit(cliente);
    return true;
  }

  private selecioneEnderecoPorAtalho(event: KeyboardEvent): boolean {
    const indice = Number(event.key) - 1;
    if (!Number.isInteger(indice) || indice < 0 || indice > 8) return false;

    const endereco = this.obtenhaEnderecoPorIndice(indice);
    if (!endereco) return false;

    event.preventDefault();
    this.enderecoIndiceAtivo = indice;
    this.selecionarEndereco.emit(endereco);
    return true;
  }

  private movaClienteAtivo(direcao: number) {
    const total = this.totalClientes;
    if (!total) return;

    const indiceAtual = this.clienteIndiceAtivo < 0 ? 0 : this.clienteIndiceAtivo;
    this.clienteIndiceAtivo = (indiceAtual + direcao + total) % total;
    this.roleClienteAtivoParaTela();
  }

  private movaEnderecoAtivo(direcao: number) {
    const total = this.enderecos?.length || 0;
    if (!total) return;

    const indiceAtual = this.enderecoIndiceAtivo < 0 ? 0 : this.enderecoIndiceAtivo;
    this.enderecoIndiceAtivo = (indiceAtual + direcao + total) % total;
    this.roleEnderecoAtivoParaTela();
  }

  private obtenhaClientePorIndice(indice: number): any {
    if (indice < 0) return null;

    const clientes = this.obtenhaClientesPlanos();
    return clientes[indice] || null;
  }

  private obtenhaEnderecoPorIndice(indice: number): any {
    if (indice < 0) return null;

    return (this.enderecos || [])[indice] || null;
  }

  private obtenhaClientesPlanos(): any[] {
    return (this.clientesAgrupados || []).reduce((clientes: any[], grupo: any) => {
      return clientes.concat(grupo?.clientes || []);
    }, []);
  }

  private foqueBusca() {
    if (this.etapaEnderecos) return;
    this.buscaCentralInput?.nativeElement?.focus();
  }

  private foqueNovoCliente() {
    this.novoClienteNomeInput?.nativeElement?.focus();
  }

  private foqueCampoVazioDoNovoCliente() {
    if (!(this.novoCliente?.nome || '').trim()) {
      this.novoClienteNomeInput?.nativeElement?.focus();
      return;
    }
    this.novoClienteTelefoneInput?.nativeElement?.focus();
  }

  private preenchaNovoClientePeloTermoBusca() {
    const termo = (this.termoBusca || '').trim();
    if (!termo) return;

    const apenasDigitos = termo.replace(/\D/g, '');
    const pareceTelefone = apenasDigitos.length >= termo.length - 4 && apenasDigitos.length >= 4;

    if (pareceTelefone) {
      this.novoCliente.telefone = this.formateTelefoneBrasileiro(apenasDigitos);
    } else {
      this.novoCliente.nome = termo;
    }
  }

  private limpeNovoCliente() {
    const lojaPadrao = this.lojasCadastroCliente[0];
    this.novoCliente = {
      nome: '',
      telefone: '',
      empresaOrigemId: lojaPadrao ? (lojaPadrao.id || lojaPadrao.empresaId) : null
    };
    this.trocaLojaCadastroVisivel = false;
  }

  private roleClienteAtivoParaTela() {
    setTimeout(() => {
      const card = this.clienteCards?.toArray()?.[this.clienteIndiceAtivo];
      card?.nativeElement?.scrollIntoView({block: 'nearest'});
    }, 0);
  }

  private roleEnderecoAtivoParaTela() {
    setTimeout(() => {
      const card = this.enderecoCards?.toArray()?.[this.enderecoIndiceAtivo];
      card?.nativeElement?.scrollIntoView({block: 'nearest'});
    }, 0);
  }
}
